import { Box, Stack, Typography } from "@mui/material";
import type { TranslationKey } from "@privacy/translations";
import { Translate, useTranslation } from "@privacy/translations";
import { Fragment, type ComponentType, type ReactNode } from "react";
import visuallyHidden from "@mui/utils/visuallyHidden";
import { HACK_alignIconWithLabel, WhiteSpace } from "@privacy/ui";
import {
  CalendarDaysIcon,
  DpiaIcon3,
  ExternalLinkIcon,
  MailIcon,
  MeasureIcon3,
  MoveRightIcon,
  PartnerIcon3,
  PersonIcon3,
  PhoneIcon,
  SparklesIcon,
  SystemIcon3,
  TenantIcon3,
  type IconProps,
} from "../shared/icons";
import { logError, logUnhandled } from "../api/logError";
import { DynamicChip } from "../dynamicShared/DynamicChip";
import { Drawer } from "../shared/Drawer";
import { Flag } from "../shared/Flag";
import { ContactPersonContent } from "../shared/ContactPerson";
import { YesOrNoInputSelectedValue } from "../dynamicForm/inlineForm/inputs/YesOrNo";
import type {
  AnswerProvenance,
  BaseHistoryEntry,
  CustomHistoryEntry,
  HistoryEntry,
  AnswerSnapshot as AnswerSnapshotType,
} from "./types";

const PADDING_X = Drawer.PADDING_X;

const defaultRenderCustomEntry = (entry: BaseHistoryEntry & CustomHistoryEntry) => {
  logError(new Error(`renderCustomEntry is not implemented for ${entry.customType}`));
  return null;
};

export const HistoryEntries = <CHE extends CustomHistoryEntry>({
  historyEntries,
  renderCustomEntry,
}: {
  historyEntries: HistoryEntry<CHE>[];

  renderCustomEntry?: (entry: BaseHistoryEntry & CHE) => ReactNode | null;
}) => {
  const resolvedRenderCustomEntry = renderCustomEntry ?? defaultRenderCustomEntry;
  const { t } = useTranslation();
  return (
    <Box sx={{ px: PADDING_X }}>
      {!historyEntries.length ? (
        <Stack sx={{ alignItems: "center", justifyContent: "center", height: "100%", py: 20 }}>
          <Box sx={{ textAlign: "center" }}>
            <Typography sx={{ fontWeight: "600", fontSize: theme => theme.typography.pxToRem(16) }}>
              {t("Registration.History.EmptyState.Title")}
            </Typography>
            <Typography
              sx={{
                fontWeight: "400",
                fontSize: theme => theme.typography.pxToRem(14),
                color: theme => theme.palette.text.tertiary,
              }}
            >
              {t("Registration.History.EmptyState.Description")}
            </Typography>
          </Box>
        </Stack>
      ) : (
        historyEntries.map(entry => (
          <Fragment key={entry.id}>
            <Stack sx={{ py: 5 }}>
              {entry.type === "questionCardHistoryEntry" ? (
                <BeforeAndAfterEntry
                  createdAt={entry.createdAt}
                  createdBy={entry.createdBy}
                  action={entry.action}
                  provenance={entry.provenance}
                  previousProvenance={entry.previousProvenance}
                  header={entry.header}
                  before={<AnswerSnapshot answerSnapshot={entry.oldAnswerSnapshot} />}
                  after={<AnswerSnapshot answerSnapshot={entry.newAnswerSnapshot} />}
                />
              ) : (
                resolvedRenderCustomEntry(entry)
              )}
            </Stack>
            <Drawer.Divider />
          </Fragment>
        ))
      )}
    </Box>
  );
};

const actionToActionKey: Record<HistoryEntry["action"], TranslationKey> = {
  modified: "History.Action.Modified",
  answered: "History.Action.Answered",
  removed: "History.Action.Removed",
};

const isAssistantWritten = (provenance: AnswerProvenance | null | undefined) =>
  provenance === "assistantInferred" || provenance === "copiedFromRecord";

const replacesAssistantDraft = (
  provenance: AnswerProvenance | null | undefined,
  previousProvenance: AnswerProvenance | null | undefined
) => !isAssistantWritten(provenance) && isAssistantWritten(previousProvenance);

export const HistoryEntryMetadata = ({
  createdAt,
  createdBy,
  action,
  provenance,
  previousProvenance,
  actionLabel,
  header,
}: {
  createdAt: string;
  createdBy: { firstName: string; lastName: string };
  action: HistoryEntry["action"];
  provenance?: AnswerProvenance | null;
  previousProvenance?: AnswerProvenance | null;
  actionLabel?: ReactNode;
  header?: ReactNode;
}) => {
  const { t, formatDate } = useTranslation();
  const note = isAssistantWritten(provenance)
    ? t("History.ViaAssistant")
    : replacesAssistantDraft(provenance, previousProvenance)
      ? t("History.ReplacingAssistantDraft")
      : null;

  return (
    <Stack>
      <Typography
        sx={{
          fontWeight: "400",
          fontSize: theme => theme.typography.pxToRem(12),
          mb: 0.5,
          color: theme => theme.palette.text.tertiary,
        }}
      >
        {formatDate(createdAt, { showTime: true })}
      </Typography>
      <Typography sx={{ fontWeight: "600", fontSize: theme => theme.typography.pxToRem(14) }}>
        {createdBy.firstName} {createdBy.lastName}
        <Typography component="span" sx={{ fontWeight: "400", fontSize: theme => theme.typography.pxToRem(14) }}>
          <WhiteSpace />
          {actionLabel ?? t(actionToActionKey[action])}
        </Typography>
        <WhiteSpace />
        {header}
        {note && (
          <Typography
            component="span"
            sx={{
              fontWeight: "400",
              fontSize: theme => theme.typography.pxToRem(14),
              color: theme => theme.palette.text.tertiary,
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              ml: 1,
            }}
          >
            <SparklesIcon size={14} />
            {note}
          </Typography>
        )}
      </Typography>
    </Stack>
  );
};

const ArchiveStatusEntry = ({
  translationKey,
  createdAt,
  createdBy,
}: {
  translationKey: TranslationKey;
  createdAt: string;
  createdBy: BaseHistoryEntry["createdBy"];
}) => {
  const { formatDate } = useTranslation();
  return (
    <Stack>
      <Typography
        sx={{
          fontWeight: "400",
          fontSize: theme => theme.typography.pxToRem(12),
          mb: 0.5,
          color: theme => theme.palette.text.tertiary,
        }}
      >
        {formatDate(createdAt, { showTime: true })}
      </Typography>
      <Typography sx={{ fontWeight: "600", fontSize: theme => theme.typography.pxToRem(14) }}>
        <Translate
          translationKey={translationKey}
          values={{
            name: (
              <Typography component="span" sx={{ fontWeight: "400", fontSize: theme => theme.typography.pxToRem(14) }}>
                {createdBy.firstName} {createdBy.lastName}
              </Typography>
            ),
            email: (
              <Typography
                component="span"
                sx={{ fontSize: theme => theme.typography.pxToRem(14), color: theme => theme.palette.text.tertiary }}
              >
                ({createdBy.email})
              </Typography>
            ),
          }}
        />
      </Typography>
    </Stack>
  );
};

export const AssistantAnswersConfirmedEntry = ({
  createdAt,
  createdBy,
  count,
}: {
  createdAt: string;
  createdBy: BaseHistoryEntry["createdBy"];
  count: number;
}) => {
  const { t, formatDate } = useTranslation();
  return (
    <Stack>
      <Typography
        sx={{
          fontWeight: "400",
          fontSize: theme => theme.typography.pxToRem(12),
          mb: 0.5,
          color: theme => theme.palette.text.tertiary,
        }}
      >
        {formatDate(createdAt, { showTime: true })}
      </Typography>
      <Typography sx={{ fontWeight: "600", fontSize: theme => theme.typography.pxToRem(14) }}>
        {createdBy.firstName} {createdBy.lastName}
        <Typography
          component="span"
          sx={{
            fontWeight: "400",
            fontSize: theme => theme.typography.pxToRem(14),
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          <WhiteSpace />
          {t("History.AssistantAnswersConfirmed", { count: String(count) })}
          <SparklesIcon size={14} />
        </Typography>
      </Typography>
    </Stack>
  );
};

export const ArchivedEntry = (props: { createdAt: string; createdBy: BaseHistoryEntry["createdBy"] }) => (
  <ArchiveStatusEntry translationKey="History.Archived" {...props} />
);

export const CreatedEntry = (props: { createdAt: string; createdBy: BaseHistoryEntry["createdBy"] }) => (
  <ArchiveStatusEntry translationKey="History.Created" {...props} />
);

export const UnarchivedEntry = (props: { createdAt: string; createdBy: BaseHistoryEntry["createdBy"] }) => (
  <ArchiveStatusEntry translationKey="History.Unarchived" {...props} />
);

export const BeforeAndAfterEntry = ({
  createdAt,
  createdBy,
  action,
  provenance,
  previousProvenance,
  header,
  before,
  after,
}: {
  createdAt: string;
  createdBy: { firstName: string; lastName: string };
  action: HistoryEntry["action"];
  provenance?: AnswerProvenance | null;
  previousProvenance?: AnswerProvenance | null;
  header?: ReactNode;
  before: ReactNode;
  after: ReactNode;
}) => {
  const { t } = useTranslation();
  return (
    <>
      <HistoryEntryMetadata
        createdAt={createdAt}
        createdBy={createdBy}
        action={action}
        provenance={provenance}
        previousProvenance={previousProvenance}
        header={header}
      />
      <Box sx={{ display: "grid", mt: 2, gap: 4, gridTemplateColumns: "1fr auto 1fr" }}>
        <Typography sx={{ fontWeight: "600", fontSize: theme => theme.typography.pxToRem(12), ...visuallyHidden }}>
          {t("RegistrationGrid.Drawer.History.Before")}
        </Typography>
        {before}

        <Stack sx={{ mt: 0.5 }}>
          <MoveRightIcon />
        </Stack>

        <Typography sx={{ fontWeight: "600", fontSize: theme => theme.typography.pxToRem(12), ...visuallyHidden }}>
          {t("RegistrationGrid.Drawer.History.After")}
        </Typography>
        {after}
      </Box>
    </>
  );
};

export const AnswerSnapshot = ({
  answerSnapshot,
  variant = "standard",
}: {
  answerSnapshot: AnswerSnapshotType;
  variant?: "standard" | "diff";
}) => {
  const { formatDate } = useTranslation();

  if (
    !answerSnapshot ||
    !answerSnapshot.value ||
    (Array.isArray(answerSnapshot.value) && answerSnapshot.value.length === 0)
  ) {
    if (variant === "diff") return null;

    return <EmptyValue />;
  }

  return (
    <Box
      sx={[
        variant === "diff" && {
          "px": 2,
          "py": 1,
          "backgroundColor": theme => theme.palette.brandOrange["50"],
          "borderRadius": 1,
          "& *": {
            transform: "scale(0.95)",
            transformOrigin: "left",
            textDecoration: "line-through",
            opacity: 0.9,
            color: theme => theme.palette.text.tertiary,
          },
        },
      ]}
    >
      {(() => {
        switch (answerSnapshot.type) {
          case "textInput":
            return <SnapshotValueText>{answerSnapshot.value}</SnapshotValueText>;
          case "yesOrNoInput": {
            return <YesOrNoInputSelectedValue value={answerSnapshot.value} />;
          }
          case "phoneNumberInput":
            return <SnapshotValueText Icon={PhoneIcon}>{answerSnapshot.value}</SnapshotValueText>;
          case "emailInput":
            return <SnapshotValueText Icon={MailIcon}>{answerSnapshot.value}</SnapshotValueText>;
          case "dateInput":
            return (
              <SnapshotValueText Icon={CalendarDaysIcon}>{formatDate(answerSnapshot.value, {})}</SnapshotValueText>
            );
          case "singleDpiaInput":
            return <SnapshotValueText Icon={DpiaIcon3}>{answerSnapshot.value}</SnapshotValueText>;
          case "multipleTenantInput":
            return (
              <MultipleValues>
                {answerSnapshot.value.map(tenant => (
                  <SnapshotValueText Icon={TenantIcon3} key={tenant.value}>
                    {tenant.name}
                  </SnapshotValueText>
                ))}
              </MultipleValues>
            );
          case "multipleExternalLinkInput":
            return (
              <MultipleValues>
                {answerSnapshot.value.map(link => (
                  <SnapshotValueText Icon={ExternalLinkIcon} key={link.url + link.label}>
                    {link.label} - {link.url}
                  </SnapshotValueText>
                ))}
              </MultipleValues>
            );
          case "externalLinkInput":
            return <SnapshotValueText Icon={ExternalLinkIcon}>{answerSnapshot.value}</SnapshotValueText>;
          case "dynamicMultipleChoiceInput":
            return (
              <MultipleValues>
                {answerSnapshot.value.map(choice => (
                  <DynamicChip variant="choice" key={choice.value} label={choice.name} />
                ))}
              </MultipleValues>
            );
          case "multiplePersonInput":
            return (
              <MultipleValues>
                {answerSnapshot.value.map(person => (
                  <SnapshotValueText Icon={PersonIcon3} key={person.id}>
                    {person.firstName} {person.lastName}
                  </SnapshotValueText>
                ))}
              </MultipleValues>
            );
          case "singleChoiceInput":
            return (
              <Box>
                <DynamicChip variant="choice" label={answerSnapshot.value} />
              </Box>
            );
          case "systemInput":
            return (
              <MultipleValues>
                {answerSnapshot.value.map(system => (
                  <SnapshotValueText Icon={SystemIcon3} key={system.value}>
                    {system.name}
                  </SnapshotValueText>
                ))}
              </MultipleValues>
            );
          case "personInput":
            return (
              <SnapshotValueText Icon={PersonIcon3}>
                {answerSnapshot.value.firstName} {answerSnapshot.value.lastName}
              </SnapshotValueText>
            );
          case "measureInput":
            return (
              <MultipleValues>
                {answerSnapshot.value.map(measure => (
                  <SnapshotValueText Icon={MeasureIcon3} key={measure.value}>
                    {measure.name}
                  </SnapshotValueText>
                ))}
              </MultipleValues>
            );
          case "multipleChoiceInput":
            return (
              <MultipleValues>
                {answerSnapshot.value.map(choice => (
                  <DynamicChip variant="choice" key={choice.value} label={choice.name} />
                ))}
              </MultipleValues>
            );
          case "countryInput":
            return (
              <MultipleValues>
                {answerSnapshot.value.map(country => (
                  <MultipleValues key={country.value}>
                    <Stack direction="row" sx={{ gap: 1, alignItems: "center" }}>
                      <Flag countryCode={country.value} />
                      <SnapshotValueText>{country.name}</SnapshotValueText>
                    </Stack>
                  </MultipleValues>
                ))}
              </MultipleValues>
            );
          case "multiplePartnerInput":
            return (
              <MultipleValues>
                {answerSnapshot.value.map(partner => (
                  <SnapshotValueText
                    Icon={partner.type === "partner" ? PartnerIcon3 : TenantIcon3}
                    key={partner.partner.value}
                  >
                    {partner.partner.name}
                  </SnapshotValueText>
                ))}
              </MultipleValues>
            );
          case "multipleContactPersonInput":
            return (
              <MultipleValues>
                {answerSnapshot.value.map(contactPerson => (
                  <ContactPersonContent
                    contactPerson={contactPerson}
                    key={contactPerson.email}
                    typographySx={{ fontSize: theme => theme.typography.pxToRem(12) }}
                  />
                ))}
              </MultipleValues>
            );
          case "multipleSubSupplierInput":
            return (
              <MultipleValues>
                {answerSnapshot.value.map((subsupplier, index) => (
                  <DynamicChip
                    variant="choice"
                    key={`${subsupplier.name}-${subsupplier.organisationNumber ?? ""}-${index}`}
                    label={subsupplier.name}
                  />
                ))}
              </MultipleValues>
            );
          default:
            logUnhandled(answerSnapshot, "input type");
        }
      })()}
    </Box>
  );
};

export const SnapshotValueText = ({ Icon, children }: { Icon?: ComponentType<IconProps>; children: ReactNode }) => {
  return (
    <Typography
      component="div"
      sx={{
        display: "flex",
        gap: 1,
        fontWeight: "400",
        fontSize: theme => theme.typography.pxToRem(14),
        color: theme => theme.palette.text.primary,
      }}
    >
      {Icon && (
        <Box component="span" sx={{ display: "flex", alignItems: "center", height: "1lh", flexShrink: 0 }}>
          <Icon color={theme => theme.palette.text.primary} size={16} />
        </Box>
      )}
      <Box component="span" sx={{ ...HACK_alignIconWithLabel, whiteSpace: "pre-wrap" }}>
        {children}
      </Box>
    </Typography>
  );
};

export const MultipleValues = ({ children }: { children: ReactNode }) => {
  return (
    <Stack spacing={1} sx={{ alignItems: "flex-start", flexWrap: "wrap" }}>
      {children}
    </Stack>
  );
};

export const EmptyValue = () => {
  const { t } = useTranslation();
  return (
    <Typography
      sx={{
        fontWeight: "400",
        fontSize: theme => theme.typography.pxToRem(14),
        color: theme => theme.palette.text.tertiary,
      }}
    >
      {t("Registration.History.EmptySnapshot.Value")}
    </Typography>
  );
};
